import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './ProjectsPageLayout.css';

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
const textOrNull = (v) => (isNonEmptyString(v) ? v.trim() : null);
const hasDisplayableCost = (v) => (typeof v === 'number') || (typeof v === 'string' && v.trim().length > 0);

function normalizeServices(services) {
  if (!services) return [];
  const arr = Array.isArray(services) ? services : [services];
  const items = arr.flatMap((s) => {
    if (typeof s === 'string') return s.split(/[,|]/).map((x) => x.trim());
    if (s && typeof s === 'object' && isNonEmptyString(s.title)) return [s.title.trim()];
    return [];
  });
  const seen = new Set();
  return items
    .map((t) => t.trim())
    .filter((t) => t.length > 0 && (seen.has(t.toLowerCase()) ? false : (seen.add(t.toLowerCase()), true)));
}

export default function ProjectsPageLayout({
  title,
  description,
  location,
  client,
  market,
  services,
  cost,
  imageSrc,
  gallery = [],
}) {
  const safeTitle = textOrNull(title) || '';
  const safeLocation = textOrNull(location);
  const safeClient = textOrNull(client);
  const safeMarket = textOrNull(market);
  const safeDescription = textOrNull(description);
  const serviceList = useMemo(() => normalizeServices(services), [services]);

  const slides = useMemo(() => {
    const list = [];
    if (isNonEmptyString(imageSrc)) list.push({ src: imageSrc.trim(), alt: `${safeTitle} main image`, caption: '' });
    if (Array.isArray(gallery) && gallery.length) {
      for (const g of gallery) {
        const src = g?.src && String(g.src).trim();
        if (src) list.push({ src, alt: (g?.alt && g.alt.trim()) || safeTitle, caption: (g?.caption || '').trim() });
      }
    }
    const seen = new Set();
    return list.filter((s) => (seen.has(s.src) ? false : (seen.add(s.src), true)));
  }, [imageSrc, gallery, safeTitle]);

  const count = slides.length;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % Math.max(count, 1));
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + Math.max(count, 1)) % Math.max(count, 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count]);

  const startX = useRef(null);
  const onTouchStart = (e) => (startX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) setIdx((i) => (i + 1) % Math.max(count, 1));
    else setIdx((i) => (i - 1 + Math.max(count, 1)) % Math.max(count, 1));
  };

  const prev = () => setIdx((i) => (i - 1 + Math.max(count, 1)) % Math.max(count, 1));
  const next = () => setIdx((i) => (i + 1) % Math.max(count, 1));

  return (
    <div className="project-page-layout">
      <div className="project-page-content">
        <h1 className="project-title">{safeTitle}</h1>
        <ul className="project-details">
          {safeLocation && <li><strong>Location:</strong> {safeLocation}</li>}
          {safeClient && <li><strong>Client:</strong> {safeClient}</li>}
          {safeMarket && <li><strong>Market:</strong> {safeMarket}</li>}
          {serviceList.length > 0 && (
            <li className="project-services">
              <strong>Services:</strong>
              <ul className="services-list" aria-label="Related services">
                {serviceList.map((s) => (
                  <li key={s} className="service-chip">{s}</li>
                ))}
              </ul>
            </li>
          )}
          {hasDisplayableCost(cost) && (
            <li>
              <strong>Cost:</strong>{' '}
              {typeof cost === 'number' ? `$${cost.toLocaleString()}` : cost.trim()}
            </li>
          )}
        </ul>
        {safeDescription && (
          <div className="project-description">
            <strong>Description:</strong>
            <div className="project-description-body">{safeDescription}</div>
          </div>
        )}
      </div>
      <div className="project-media">
        {count > 0 ? (
          <div
            className="carousel"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            aria-label="Project images carousel"
          >
            <div className="carousel-viewport">
              <img
                key={slides[idx].src}
                src={slides[idx].src}
                alt={slides[idx].alt || safeTitle}
                className="carousel-image"
                loading="lazy"
              />
              {count > 1 && (
                <>
                  <button className="carousel-nav left" onClick={prev} aria-label="Previous image">‹</button>
                  <button className="carousel-nav right" onClick={next} aria-label="Next image">›</button>
                </>
              )}
            </div>
            {(slides[idx].caption || '').trim() && (
              <div className="carousel-caption">{slides[idx].caption}</div>
            )}
          </div>
        ) : (
          <div className="carousel-empty">No images available</div>
        )}
        {count > 1 && (
          <>
            <div className="carousel-dots" role="tablist">
              {slides.map((s, i) => (
                <button
                  key={s.src}
                  role="tab"
                  aria-selected={i === idx}
                  className={`dot ${i === idx ? 'active' : ''}`}
                  onClick={() => setIdx(i)}
                  title={`Image ${i + 1}`}
                />
              ))}
            </div>
            <div className="carousel-thumbs">
              {slides.map((s, i) => (
                <button
                  key={`thumb-${s.src}`}
                  className={`thumb ${i === idx ? 'current' : ''}`}
                  onClick={() => setIdx(i)}
                  aria-label={`Go to image ${i + 1}`}
                >
                  <img src={s.src} alt={s.alt || `${safeTitle} thumbnail ${i + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

ProjectsPageLayout.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  location: PropTypes.string,
  client: PropTypes.string,
  market: PropTypes.string,
  services: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ title: PropTypes.string })
      ])
    ),
    PropTypes.string
  ]),
  cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  imageSrc: PropTypes.string,
  gallery: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    caption: PropTypes.string,
  })),
};
